import React, { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
    Grid,
    Dialog,
    DialogContent,
    Checkbox,
    MenuItem,
    TextField,
    Switch,
    FormControlLabel,
    InputAdornment,
    IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { endOfDay, format, isValid } from 'date-fns';
import { ModalTitle, PrimaryButton, SecondaryButton, StickyModalActions, SectionTitle } from '@/styles/blocks';
import { colors } from '@/styles';
import { Body1 } from '../blocks';

const pickerTheme = createTheme({ palette: { primary: { main: colors.greens.primary } } });

const toDate = (value) => (value ? new Date(value) : null);

const sameTime = (a, b) => (a ? new Date(a).getTime() : null) === (b ? new Date(b).getTime() : null);

const buildDraft = (filter = {}) => ({
    completedStart: toDate(filter.completedStart),
    completedEnd: toDate(filter.completedEnd),
    shippers: filter.shippers || [],
    carriers: filter.carriers || [],
    week: filter.week,
});

const hasCustomDateRange = (filter = {}, defaults = {}) =>
    !filter.week && (Boolean(filter.completedEnd) || !sameTime(filter.completedStart, defaults.completedStart));

const MultiSelectFilter = ({ label, options, value, onChange }) => {
    const map = useMemo(() => Object.fromEntries(options.map((opt) => [opt.value, opt.label])), [options]);
    return (
        <TextField
            variant="outlined"
            select
            fullWidth
            label={label}
            value={value}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => onChange(e.target.value)}
            SelectProps={{
                multiple: true,
                renderValue: (selected) =>
                    selected
                        .map((opt) => map[opt])
                        .filter(Boolean)
                        .join(', '),
            }}
            css={css`
                background-color: white;
            `}
        >
            {options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox color="primary" checked={value.indexOf(opt.value) > -1} />
                    <Body1>{opt.label}</Body1>
                </MenuItem>
            ))}
        </TextField>
    );
};

const CompletedOrdersFilterDialog = ({ open, state, callbacks, onClose }) => {
    const { filter, defaultFilter, shippers = [], carriers = [], weeks = [] } = state;
    const [draft, setDraft] = useState(() => buildDraft(filter));
    const [advancedDates, setAdvancedDates] = useState(() => hasCustomDateRange(filter, defaultFilter));

    useEffect(() => {
        if (open) {
            setDraft(buildDraft(filter));
            setAdvancedDates(hasCustomDateRange(filter, defaultFilter));
        }
    }, [open, filter, defaultFilter]);

    const endBeforeStart =
        isValid(draft.completedStart) && isValid(draft.completedEnd) && draft.completedEnd < draft.completedStart;
    const datesValid =
        (!draft.completedStart || isValid(draft.completedStart)) && (!draft.completedEnd || isValid(draft.completedEnd));

    // Effective date range the table will show once this draft is applied.
    // End is open-ended when unset; today is the effective upper bound since
    // completion times can't be in the future.
    const viewRange = useMemo(() => {
        if (advancedDates) {
            return {
                start: isValid(draft.completedStart) ? draft.completedStart : toDate(defaultFilter?.completedStart),
                end: isValid(draft.completedEnd) ? draft.completedEnd : new Date(),
            };
        }
        if (draft.week) {
            return { start: draft.week.sow, end: draft.week.eow };
        }
        return { start: toDate(defaultFilter?.completedStart), end: new Date() };
    }, [advancedDates, draft.completedStart, draft.completedEnd, draft.week, defaultFilter]);

    const toggleAdvanced = (on) => {
        setAdvancedDates(on);
        setDraft((prev) => (on ? { ...prev, week: undefined } : { ...prev, completedStart: null, completedEnd: null }));
    };

    const apply = () => {
        const next = { ...draft };
        if (advancedDates) {
            next.week = undefined;
            if (next.completedEnd) {
                next.completedEnd = endOfDay(next.completedEnd);
            }
        } else {
            next.completedStart = null;
            next.completedEnd = null;
        }
        if (!next.week && !next.completedStart && !next.completedEnd) {
            next.completedStart = defaultFilter?.completedStart ?? null;
        }
        callbacks.setFilter((prev) => ({ ...prev, ...next }));
        onClose();
    };

    const removeAll = () => {
        callbacks.setFilter((prev) => ({
            ...prev,
            completedStart: defaultFilter?.completedStart ?? null,
            completedEnd: defaultFilter?.completedEnd ?? null,
            shippers: [],
            carriers: [],
            week: undefined,
        }));
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth={true}
            maxWidth="sm"
            scroll="paper"
            css={css`
                .MuiDialog-paper {
                    height: auto;
                    max-height: 85vh;
                }
            `}
        >
            <ModalTitle border onClose={onClose}>
                <Grid
                    container
                    css={css`
                        align-items: center;
                        gap: 16px;
                        flex-wrap: nowrap;
                    `}
                >
                    <SectionTitle
                        css={css`
                            padding-top: 15px;
                        `}
                    >
                        Filters
                    </SectionTitle>
                    <FormControlLabel
                        control={
                            <Switch
                                edge="start"
                                checked={advancedDates}
                                onChange={(e) => toggleAdvanced(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Custom Date Range"
                        css={css`
                            margin: 0;
                        `}
                    />
                </Grid>
            </ModalTitle>
            <DialogContent
                css={css`
                    width: 100%;
                    padding: 0;
                `}
            >
                <Grid
                    container
                    direction="column"
                    css={css`
                        padding: 32px 24px;
                        gap: 20px;
                    `}
                >
                    <MultiSelectFilter
                        label="Shipper"
                        options={shippers}
                        value={draft.shippers}
                        onChange={(value) => setDraft((prev) => ({ ...prev, shippers: value }))}
                    />

                    <MultiSelectFilter
                        label="Carrier"
                        options={carriers}
                        value={draft.carriers}
                        onChange={(value) => setDraft((prev) => ({ ...prev, carriers: value }))}
                    />

                    {advancedDates ? (
                        <ThemeProvider theme={pickerTheme}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <Grid
                                    container
                                    direction="row"
                                    css={css`
                                        gap: 12px;
                                        flex-wrap: nowrap;
                                    `}
                                >
                                    <DatePicker
                                        label="Completed Start"
                                        format="MM/dd/yyyy"
                                        value={draft.completedStart}
                                        onChange={(date) => setDraft((prev) => ({ ...prev, completedStart: date }))}
                                        slotProps={{
                                            textField: { fullWidth: true, InputLabelProps: { shrink: true } },
                                        }}
                                    />
                                    <DatePicker
                                        label="Completed End"
                                        format="MM/dd/yyyy"
                                        value={draft.completedEnd}
                                        minDate={isValid(draft.completedStart) ? draft.completedStart : undefined}
                                        onChange={(date) => setDraft((prev) => ({ ...prev, completedEnd: date }))}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                InputLabelProps: { shrink: true },
                                                error: endBeforeStart,
                                                helperText: endBeforeStart
                                                    ? 'End date must be on or after the start date'
                                                    : '',
                                            },
                                        }}
                                    />
                                </Grid>
                            </LocalizationProvider>
                        </ThemeProvider>
                    ) : (
                        <TextField
                            variant="outlined"
                            select
                            fullWidth
                            label="Pay Period"
                            value={draft.week?.week_number || ''}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment
                                        position="end"
                                        css={css`
                                            margin-right: 20px;
                                        `}
                                    >
                                        <IconButton
                                            css={css`
                                                padding: 4px;
                                            `}
                                            onClick={() => setDraft((prev) => ({ ...prev, week: undefined }))}
                                        >
                                            <CloseIcon
                                                css={css`
                                                    font-size: 14px;
                                                `}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            onChange={(e) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    week: weeks.find(({ week_number }) => week_number === e.target.value),
                                }))
                            }
                            css={css`
                                background-color: white;
                            `}
                        >
                            {weeks.map(({ week_number, label }) => (
                                <MenuItem key={week_number} value={week_number}>
                                    <Body1>{label}</Body1>
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    {viewRange.start && viewRange.end && (
                        <Body1
                            css={css`
                                color: ${colors.greys.primary};
                                font-size: 12px;
                            `}
                        >
                            {`Current view is limited to ${format(viewRange.start, 'MM/dd/yyyy')} to ${format(
                                viewRange.end,
                                'MM/dd/yyyy'
                            )}`}
                        </Body1>
                    )}
                </Grid>
            </DialogContent>

            <StickyModalActions>
                <SecondaryButton onClick={removeAll}>Remove all filters</SecondaryButton>
                <Grid
                    item
                    css={css`
                        display: flex;
                        gap: 8px;
                    `}
                >
                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                    <PrimaryButton onClick={apply} disabled={advancedDates && (endBeforeStart || !datesValid)}>
                        Apply
                    </PrimaryButton>
                </Grid>
            </StickyModalActions>
        </Dialog>
    );
};

export default CompletedOrdersFilterDialog;
